import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from 'layouts/member/Layout';
import { IconChevronLeft, IconChevronRight, IconClose } from 'components/Svg';
import SubmitButton from 'components/SubmitButton';

import iconWater from 'assets/themes/default/menu_water.png';
import iconLight from 'assets/themes/default/menu_light.png';
import iconAircon from 'assets/themes/default/menu_aircon.png';
import iconPest from 'assets/themes/default/menu_pest.png';
import iconKitchen from 'assets/themes/default/menu_kitchen.png';

const iconMap: { [key: string]: string } = {
  menu_water: iconWater,
  menu_light: iconLight,
  menu_aircon: iconAircon,
  menu_pest: iconPest,
  menu_kitchen: iconKitchen,
};

// URLのpathからインデックス配列を取得
const getIndexesFromPath = (pathname: string) => {
  // 例: /service/0/1/2/3
  const match = pathname.match(/\/service((?:\/\d+)+)/);
  if (!match) return [];
  return match[1].split('/').filter(Boolean).map(Number);
};

type Estimate = {
  label: string;
  price: string;
};

type Condition = {
  label: string;
  estimates: Estimate;
};

type Part = {
  label: string;
  conditions: Condition[];
};

type Location = {
  label: string;
  parts: Part[];
};

type MenuItem = {
  title: string;
  icon: string;
  description?: string[] | string;
  locations?: Location[];
};

type MenuJson = {
  menu: MenuItem[];
};

const Service: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const indexes = getIndexesFromPath(location.pathname); // [menuIndex, locationIndex, partIndex, conditionIndex]
  const [menuList, setMenuList] = useState<MenuItem[]>([]);

  useEffect(() => {
    fetch('/menu_list.json')
      .then(res => res.json())
      .then((data: MenuJson) => setMenuList(data.menu));
  }, []);

  // 最上位階層
  const isTop = indexes.length === 0;
  const menu = menuList[indexes[0]];

  // 階層ごとに現在のデータを取得
  let list: (MenuItem | Location | Part | Condition)[] = [];
  let headerTitle = '';
  let headerIcon = '';
  let description: string[] | string | undefined = undefined;
  let isLast = false;
  let estimateBlock = null;

  if (isTop) {
    // 最上位階層: メニューリスト
    list = menuList;
  } else if (menu) {
    headerTitle = menu.title;
    headerIcon = menu.icon;
    description = menu.description;

    const current: MenuItem = menu;
    // locations
    if (indexes.length === 1 && current.locations) {
      list = current.locations;
    }
    // parts
    if (indexes.length === 2 && current.locations) {
      const locationObj = current.locations[indexes[1]];
      list = locationObj?.parts || [];
      headerTitle = locationObj?.label || headerTitle;
    }
    // conditions
    if (indexes.length === 3 && current.locations) {
      const locationObj = current.locations[indexes[1]];
      const partObj = locationObj?.parts[indexes[2]];
      list = partObj?.conditions || [];
      headerTitle = partObj?.label || headerTitle;
    }
    // estimates（最下層）
    if (indexes.length === 4 && current.locations) {
      const locationObj = current.locations[indexes[1]];
      const partObj = locationObj?.parts[indexes[2]];
      const conditionObj = partObj?.conditions[indexes[3]];
      isLast = true;
      headerTitle = conditionObj?.label || headerTitle;
      estimateBlock = conditionObj && (
        <div className="p-4 mt-6 rounded shadow bg-blue-50">
          <div className="mb-2 font-bold text-gray-800">予想対応内容</div>
          <div className="text-sm text-gray-700">{conditionObj.estimates.label}</div>
          <div className="mt-2 text-sm font-bold text-blue-800">{conditionObj.estimates.price}</div>
        </div>
      );
    }
  }

  // 戻るボタンのパス
  const handleBack = () => {
    if (indexes.length === 0) return;
    if (indexes.length === 1) {
      navigate('/');
      return;
    }
    const prevPath = '/service' + indexes.slice(0, -1).map(i => `/${i}`).join('');
    navigate(prevPath || '/');
  };

  // 子リストクリック時の遷移
  const handleNext = (idx: number) => {
    const nextPath = '/service' + indexes.map(i => `/${i}`).join('') + `/${idx}`;
    navigate(nextPath);
  };

  // description表示（最下層のみ別枠・連番・小文字サイズ）
  let descriptionBlock = null;
  if (isLast && description) {
    descriptionBlock = (
      <div className="p-4 mt-6 bg-white rounded shadow">
        <div className="mb-2 text-sm font-bold text-gray-800">ご注意事項</div>
        {Array.isArray(description)
          ? description.map((desc, i) => (
              <div key={i} className="mb-1 text-xs">
                {`${i + 1}. ${desc}`}
              </div>
            ))
          : <div className="text-xs">{description}</div>}
      </div>
    );
  }

  // ステップフロー用
  const stepLabels = ['発生場所', '故障箇所', '状態', '見積り'];

  const currentStep = Math.min(Math.max(indexes.length - 1, 0), 3);

  // アイコン
  const iconSrc = iconMap[headerIcon] || iconWater;

  return (
    <Layout>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <button className="p-2" onClick={handleBack} disabled={isTop}>
            <IconChevronLeft />
          </button>
          <div className="flex flex-col items-center flex-1">
            {isTop ? (
              <>
                <span className="text-lg font-medium text-gray-900">メニュー選択</span>
              </>
            ) : (
              <>
                <span className="text-lg font-medium text-gray-900">{headerTitle}</span>
              </>
            )}
          </div>
          <button className="p-2" onClick={() => navigate('/')}>
            <IconClose />
          </button>
        </div>

        {!isTop && (
          <div className="flex justify-center mt-3">
            <img
              src={iconSrc}
              alt={headerTitle}
              className="object-contain w-16 h-16"
            />
          </div>
        )}

        {/* ステップフロー */}
        <div className="flex flex-col items-center mt-4 mb-2">
          <div className="flex items-center w-full max-w-md">
            {stepLabels.map((label, idx) => (
              <React.Fragment key={label}>
                <div className="relative flex flex-col items-center">
                  {/* ステップ番号・チェック・2重丸 */}
                  {idx < currentStep ? (
                    // チェックマーク
                    <div className="z-10 flex items-center justify-center font-bold text-white border-2 rounded-full w-9 h-9 bg-primary border-primary">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : idx === currentStep ? (
                    // 2重丸
                    <span className="relative flex items-center justify-center w-9 h-9">
                      <span className="absolute border-2 rounded-full w-9 h-9 border-primary"></span>
                      <span className="z-10 flex items-center justify-center font-bold text-white rounded-full w-7 h-7 bg-primary">
                        {idx + 1}
                      </span>
                    </span>
                  ) : (
                    // 未到達
                    <div className="z-10 flex items-center justify-center font-bold text-gray-500 bg-gray-200 border border-gray-300 rounded-full w-9 h-9">
                      {idx + 1}
                    </div>
                  )}
                  <span
                    className={
                      "mt-1 text-xs " +
                      (idx === currentStep ? "text-primary font-bold" : "text-gray-500")
                    }
                  >
                    {label}
                  </span>
                </div>
                {idx < stepLabels.length - 1 && (
                  <div className="relative flex items-center justify-center flex-1" style={{ minWidth: 32 }}>
                    <div
                      className="w-12 h-0.5 bg-gray-300"
                      style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* 階層リスト */}
        {!isLast && (
          <ul className="mt-6 space-y-2">
            {list.map((item, idx) => (
              <li key={'label' in item ? item.label : ('title' in item ? item.title : idx)}>
                <button
                  className="flex items-center justify-between w-full px-3 py-4 bg-white rounded-lg shadow"
                  onClick={() => handleNext(idx)}
                >
                  <span className="text-gray-800">{'label' in item ? item.label : ('title' in item ? item.title : '')}</span>
                  <IconChevronRight />
                </button>
              </li>
            ))}
          </ul>
        )}

        {estimateBlock}
        {descriptionBlock}

        {isLast && (
          <div className="flex justify-center mt-8">
            <SubmitButton
              onClick={() => {
                navigate('/rescue');
              }}
            >
              最終確認へ
            </SubmitButton>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Service;